import { BulkActionBar } from '@app/components/data-table/bulk-edit/bulk-action-bar';
import useAuth from '@app/auth/use-auth';
import { makeSelectColumn } from '@app/components/data-table/bulk-edit/select-column';
import { DataTable } from '@app/components/data-table/data-table';
import { useServerDataTable } from '@app/components/data-table/hooks/use-server-data-table';
import { formatSortingForEndpoint } from '@app/components/data-table/lib/parsers';
import { useDataTableQueryState } from '@app/hooks/use-data-table-query-state';
import { useAccount } from '@shared/api';
import { useGetPurchasedOtherList } from '@shared/api/hooks/purchased-goods/other';
import {
  PurchasedGoodsOtherListParams,
  PurchasedGoodsOtherListSortItem,
  UncategorisedPurchasedGood,
} from '@shared/api/types';
import { ActivityCell } from '@shared/components/data-table/cells/activity-cell';
import { HeaderCell } from '@shared/components/data-table/cells/header-cell';
import { NumberCell } from '@shared/components/data-table/cells/number-cell';
import { TextCell } from '@shared/components/data-table/cells/text-cell';
import { formatCost } from '@shared/lib/format';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { uncategorisedFilterColumns, uncategorisedFilters } from '../filters';
import { UpdateItemButton } from '@app/components/item-edit/update-item-button';
import { makeUpdateItemsAction } from '@app/components/item-edit/update-items-modal-body';

export default function ExcludedOtherTab() {
  const { page, perPage, sort, search, ...filterStates } =
    useDataTableQueryState({
      filters: ['search', ...uncategorisedFilterColumns],
    });
  const { user } = useAuth();
  const { data: account } = useAccount(true);
  const currency = account?.defaultCurrency;

  const sortItems = (sort ?? [])
    .map(formatSortingForEndpoint)
    .filter(Boolean) as PurchasedGoodsOtherListSortItem[];

  const params: PurchasedGoodsOtherListParams = {
    search: search ?? undefined,
    page,
    page_size: perPage,
    // Default to highest spend first; an explicit user sort wins.
    sort: sortItems.length
      ? sortItems
      : [PurchasedGoodsOtherListSortItem['-totalSpend']],
  };
  const paramRecord = params as Record<string, unknown>;
  const filterRecord = filterStates as Record<string, unknown>;
  uncategorisedFilters.forEach(({ apiKey, column }) => {
    paramRecord[apiKey] = filterRecord[column];
  });

  const { data, isLoading, isPreviousData, isFetchedAfterMount } =
    useGetPurchasedOtherList(params);

  const pageCount = data?.count ? Math.ceil(data.count / (perPage || 10)) : 0;

  const columns = useMemo<ColumnDef<UncategorisedPurchasedGood>[]>(
    () => [
      // Selection checkboxes are staff-only (bulk actions are gated to staff).
      ...(user?.isStaff
        ? [makeSelectColumn<UncategorisedPurchasedGood>()]
        : []),
      {
        id: 'name',
        accessorKey: 'name',
        header: ({ column }) => <HeaderCell column={column} name="Name" />,
        cell: ({ row }) => (
          <ActivityCell
            name={row.original.name}
            remoteId={row.original.externalId ?? undefined}
          />
        ),
        // The activity list does not support ordering by name (only quantity /
        // total spend / created etc.), so name is not sortable here.
        enableSorting: false,
        size: 280,
        meta: { label: 'Name', sorting: { ascLabel: 'A-Z', descLabel: 'Z-A' } },
      },
      {
        id: 'quantity',
        accessorKey: 'quantity',
        header: ({ column }) => (
          <HeaderCell column={column} name="Quantity" numberColumn />
        ),
        cell: ({ getValue }) => {
          const value = getValue<number | null>();
          return value == null ? (
            <TextCell text="—" type="number" />
          ) : (
            <NumberCell number={value} rounded />
          );
        },
      },
      {
        id: 'totalSpend',
        accessorKey: 'totalSpend',
        header: ({ column }) => (
          <HeaderCell column={column} name="Total spend" numberColumn />
        ),
        cell: ({ getValue }) => {
          const value = getValue<number | null>();
          return (
            <TextCell
              text={formatCost(value ?? null, currency)}
              type="number"
            />
          );
        },
      },
      {
        id: 'actions',
        header: ({ column }) => <HeaderCell column={column} name="Status" />,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <UpdateItemButton
              itemUuid={row.original.purchasedGoodUuid}
              currentType="other"
              itemDetails={{
                name: row.original.name,
                externalId: row.original.externalId,
                inputUnit: row.original.inputUnit,
                itemsPerPack: row.original.itemsPerPack,
                itemMassOrVolume: row.original.itemMassOrVolume,
                itemUnitSymbol: row.original.itemUnit,
                inputMode: row.original.inputMode,
                quantity: row.original.quantity,
                totalSpend: row.original.totalSpend,
              }}
            />
          </div>
        ),
      },
    ],
    [currency, user?.isStaff]
  );

  const { table } = useServerDataTable<UncategorisedPurchasedGood>({
    data: data?.results || [],
    columns,
    pageCount,
    shallow: false,
    getRowId: (row) => row.purchasedGoodUuid,
    // Mirrors the API-side default so the header indicator matches the data.
    initialState: { sorting: [{ id: 'totalSpend', desc: true }] },
  });

  const bulkActions = useMemo(
    () => [
      makeUpdateItemsAction({
        currentType: 'other',
      }),
    ],
    []
  );

  return (
    <>
      <DataTable
        table={table}
        className="border border-gray-100 bg-gray-00 text-left"
        isLoading={(!isFetchedAfterMount && isPreviousData) || isLoading}
      />
      <BulkActionBar table={table} actions={bulkActions} />
    </>
  );
}
